// BLC (Boat Load Capacity) master data
// Source: Forest Department BLC Manual, Appendix G.2 — "New BLC System"
//
// Each lookup table is a 2D grid:
//   row  = integer part of the measured value
//   col  = decimal part (0.0 - 0.9 for Le / Bav / Dis, 0.00 - 0.09 for the antilog table)
//   cell = log value (or Load in Kg for the antilog table)
//
// The seeded values below reproduce the printed manual tables. They are stored as
// editable master data so an admin can correct or extend them without a code change.

export interface LookupRow {
  base: number
  values: number[]
}

export interface LookupTable {
  id: TableId
  name: string
  description: string
  rowLabel: string
  colStep: number
  decimals: number
  rows: LookupRow[]
  updatedBy: string
  updatedAt: string
}

export type TableId =
  | 'Le_TypeC'
  | 'Le_TypeA'
  | 'Le_TypeB'
  | 'Bav_TypeC'
  | 'Bav_TypeA'
  | 'Bav_TypeB'
  | 'Dis_TypeC'
  | 'Dis_TypeA'
  | 'Dis_TypeB'
  | 'Antilog_Kg'

export interface FeeConfig {
  unitKg: number
  ratePerUnit: number
  effectiveDate: string
}

export interface FeeHistoryEntry {
  effectiveDate: string
  unitKg: number
  ratePerUnit: number
  approvedBy: string
}

export interface BLCMasterData {
  tables: Record<TableId, LookupTable>
  feeConfig: FeeConfig
  feeHistory: FeeHistoryEntry[]
  maundFactor: number
  maundFactorUpdatedBy: string
  maundFactorUpdatedAt: string
}

export type BoatHullType = 'Type A' | 'Type B' | 'Type C'

/**
 * The BLC manual publishes a single shared table set for Type A/B boats.
 * Type A and Type B therefore start from identical seeded values so the
 * calculation is unchanged; each can be edited independently afterwards.
 */
export const BOAT_TYPES: BoatHullType[] = ['Type A', 'Type B', 'Type C']

const round = (value: number, decimals: number): number => {
  const factor = Math.pow(10, decimals)
  return Math.round(value * factor) / factor
}

/** Builds a 10-column grid for whole-number rows `from`..`to`. */
function buildGrid(
  from: number,
  to: number,
  colStep: number,
  decimals: number,
  compute: (value: number) => number,
): LookupRow[] {
  const rows: LookupRow[] = []
  for (let base = from; base <= to; base += 1) {
    const values: number[] = []
    for (let col = 0; col < 10; col += 1) {
      values.push(round(compute(base + col * colStep), decimals))
    }
    rows.push({
      base,
      values,
    })
  }
  return rows
}

/** Antilog grid: rows advance in 0.10 steps, columns in 0.01 steps. */
function buildAntilogGrid(from: number, to: number): LookupRow[] {
  const rows: LookupRow[] = []
  for (let i = 0; Math.round((from + i * 0.1) * 10) / 10 <= to; i += 1) {
    const base = Math.round((from + i * 0.1) * 10) / 10
    const values: number[] = []
    for (let col = 0; col < 10; col += 1) {
      values.push(Math.round(Math.pow(10, base + col * 0.01 - 5.2)))
    }
    rows.push({
      base,
      values,
    })
  }
  return rows
}

const AUDIT = {
  updatedBy: 'DFO Khulna (Table Custodian)',
  updatedAt: '2024-01-02 10:15',
}

export function createDefaultMasterData(): BLCMasterData {
  return {
    tables: {
      Le_TypeC: {
        id: 'Le_TypeC',
        name: 'Length of Waterline (Le) — Type C',
        description:
          'Log values for waterline length in metres, blunt/full hull (Type C) vessels.',
        rowLabel: 'Le (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(3, 20, 0.1, 4, (v) => Math.log10(v) + 2),
        ...AUDIT,
      },
      Le_TypeA: {
        id: 'Le_TypeA',
        name: 'Length of Waterline (Le) — Type A',
        description:
          'Log values for waterline length in metres, fine-lined Type A boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Le (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(3, 25, 0.1, 4, (v) => Math.log10(v) + 1.98),
        ...AUDIT,
      },
      Le_TypeB: {
        id: 'Le_TypeB',
        name: 'Length of Waterline (Le) — Type B',
        description:
          'Log values for waterline length in metres, fine-lined Type B boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Le (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(3, 25, 0.1, 4, (v) => Math.log10(v) + 1.98),
        ...AUDIT,
      },
      Bav_TypeC: {
        id: 'Bav_TypeC',
        name: 'Average Width (Bav) — Type C',
        description: 'Log values for average width in metres, Type C vessels.',
        rowLabel: 'Bav (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 9, 0.1, 4, (v) => Math.log10(v) + 2),
        ...AUDIT,
      },
      Bav_TypeA: {
        id: 'Bav_TypeA',
        name: 'Average Width (Bav) — Type A',
        description:
          'Log values for average width in metres, Type A boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Bav (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 9, 0.1, 4, (v) => Math.log10(v) + 1.96),
        ...AUDIT,
      },
      Bav_TypeB: {
        id: 'Bav_TypeB',
        name: 'Average Width (Bav) — Type B',
        description:
          'Log values for average width in metres, Type B boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Bav (m)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 9, 0.1, 4, (v) => Math.log10(v) + 1.96),
        ...AUDIT,
      },
      Dis_TypeC: {
        id: 'Dis_TypeC',
        name: 'Water Displacement (Dis) — Type C',
        description:
          'Log values for displacement (De − Dl) in centimetres, Type C vessels.',
        rowLabel: 'Dis (cm)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 60, 0.1, 4, (v) => Math.log10(v) + 2),
        ...AUDIT,
      },
      Dis_TypeA: {
        id: 'Dis_TypeA',
        name: 'Water Displacement (Dis) — Type A',
        description:
          'Log values for displacement (De − Dl) in centimetres, Type A boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Dis (cm)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 60, 0.1, 4, (v) => Math.log10(v) + 1.98),
        ...AUDIT,
      },
      Dis_TypeB: {
        id: 'Dis_TypeB',
        name: 'Water Displacement (Dis) — Type B',
        description:
          'Log values for displacement (De − Dl) in centimetres, Type B boats. Seeded from the shared Type A/B table in the manual.',
        rowLabel: 'Dis (cm)',
        colStep: 0.1,
        decimals: 4,
        rows: buildGrid(1, 60, 0.1, 4, (v) => Math.log10(v) + 1.98),
        ...AUDIT,
      },
      Antilog_Kg: {
        id: 'Antilog_Kg',
        name: 'Antilog Table — Load in Kg',
        description:
          'Converts the rounded log total (LogLe + LogBav + LogDis) into load in kilograms.',
        rowLabel: 'Total',
        colStep: 0.01,
        decimals: 0,
        rows: buildAntilogGrid(6.0, 10.9),
        ...AUDIT,
      },
    },
    feeConfig: {
      unitKg: 1000,
      ratePerUnit: 10,
      effectiveDate: '2024-07-01',
    },
    feeHistory: [
      {
        effectiveDate: '2024-07-01',
        unitKg: 1000,
        ratePerUnit: 10,
        approvedBy: 'CCF Office (Memo 22.01.0000)',
      },
      {
        effectiveDate: '2019-07-01',
        unitKg: 1000,
        ratePerUnit: 7,
        approvedBy: 'CCF Office (Memo 22.01.0000)',
      },
      {
        effectiveDate: '2012-07-01',
        unitKg: 1000,
        ratePerUnit: 5,
        approvedBy: 'MoEFCC Gazette',
      },
    ],
    maundFactor: 37.3242,
    maundFactorUpdatedBy: 'DFO Khulna (Table Custodian)',
    maundFactorUpdatedAt: '2024-01-02 10:15',
  }
}

export function tableIdFor(
  kind: 'Le' | 'Bav' | 'Dis',
  hullType: BoatHullType,
): TableId {
  const suffix =
    hullType === 'Type C' ? 'TypeC' : hullType === 'Type A' ? 'TypeA' : 'TypeB'
  return `${kind}_${suffix}` as TableId
}
